import { User, ArrowRight, ArrowDownUp, RotateCcw, CheckCircle2, Lock, Briefcase } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Steps, pathFor, Callout,
  Diagram, MockSelect, MockField, MockButton,
} from "../shared";

export default function ResponsibilitySection() {
  return (
    <div data-testid="guide-responsibility">
      <PageHeading
        icon={User}
        title="Responsibility & Handoffs"
        description="Every action item has two people-roles: the Owner (who is accountable) and Currently With (who has the work right now). Telling these two apart is the single most important idea in the whole system."
      />

      <Callout variant="info" title="In plain words">
        Think of a relay race. The <span className="text-white">Owner</span> is the team captain who is responsible for finishing the race — that never changes. <span className="text-white">Currently With</span> is whoever is holding the baton at this exact moment — that changes every time someone passes it on. Passing the baton is called a <span className="text-white">handoff</span>.
      </Callout>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          You use a handoff whenever you've finished your part and someone else needs to take over before the next step. The owner stays the same; only "who has the baton" changes.
        </p>
      </Section>

      <Section title="Owner vs Currently With">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="p-3 rounded-md bg-white/[0.02]" data-testid="text-role-owner">
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="w-4 h-4 text-cyan-400" />
              <p className="text-sm text-white font-medium">Owner (Assigned To)</p>
            </div>
            <p className="text-xs text-white/55 leading-relaxed">
              The person ultimately accountable. Chosen from the team members list when the item is registered, and it stays the same for the item's whole life. The owner's team and function fill in automatically.
            </p>
          </div>
          <div className="p-3 rounded-md bg-white/[0.02]" data-testid="text-role-currently-with">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-[#FF9F43]" />
              <p className="text-sm text-white font-medium">Currently With</p>
            </div>
            <p className="text-xs text-white/55 leading-relaxed">
              Whoever has the work right now. This changes as the item moves between people and lanes, and every change is recorded.
            </p>
          </div>
        </div>
      </Section>

      <Section title="Handing off: passing the baton">
        <p className="text-sm text-white/65 leading-relaxed">
          A handoff changes who currently holds the item — handy when you've done your bit and someone else takes over before the next status step. The owner does not change. For the full step-by-step, see <Link href={pathFor("forms/handoff")} className="text-cyan-300 hover:underline" data-testid="link-handoff-walkthrough">Hand Off Responsibility</Link>.
        </p>
        <Diagram
          title="The hand-off controls (on Updates or the detail page)"
          frameLabel="Hand off responsibility"
          legend={[
            <>Open the <span className="text-white">"Hand to"</span> dropdown next to the status selector.</>,
            <>Pick a <span className="text-white">team member</span> or an <span className="text-white">authority lane</span> name from the list.</>,
            <>Optionally type a <span className="text-white">handoff note</span> saying what the next person should do.</>,
            <>Click <span className="text-white">Hand off</span> — the receiver is emailed (if email is set up) and the change is logged.</>,
          ]}
        >
          <MockSelect
            label="Hand to"
            value="Select a person or lane…"
            marker={1}
            open
            options={["Amal R. (team member)", "Sara K. (team member)", "EDGE Chairman (lane)"]}
          />
          <MockField label="Handoff note (optional)" placeholder="What should the next person do?" marker={3} />
          <MockButton label="Hand off" marker={4} />
        </Diagram>
        <Steps
          steps={[
            <>Open the <span className="text-white">"Hand to"</span> dropdown next to the status selector (admins only).</>,
            <>Choose from the list — it shows both <span className="text-white">team members</span> and <span className="text-white">authority lane names</span> (e.g. "EDGE Chairman").</>,
            <>Optionally add a <span className="text-white">handoff note</span> explaining what's needed next.</>,
            <>Submit. The history records who had it before, who got it, your note, and when — a clear chain of custody. The Summary page then shows <span className="text-[#FF9F43]">"With: [name]"</span> in orange, and the receiver gets an email if email is set up.</>,
          ]}
        />
      </Section>

      <Section title="Which action should I use?">
        <div className="space-y-3 text-xs text-white/60">
          <div className="flex items-start gap-2">
            <ArrowRight className="w-3.5 h-3.5 text-cyan-400 mt-0.5 shrink-0" />
            <p><span className="text-white font-medium">Handoff:</span> pass the item to someone else for the next step. Owner stays the same. Example: an analyst passes it to the MD for review.</p>
          </div>
          <div className="flex items-start gap-2">
            <ArrowDownUp className="w-3.5 h-3.5 text-purple-400 mt-0.5 shrink-0" />
            <p><span className="text-white font-medium">Status Change:</span> the item reaches a new stage. Example: In Progress → With SDF MD Review. You can do a status change and a handoff together in one action.</p>
          </div>
          <div className="flex items-start gap-2">
            <RotateCcw className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
            <p><span className="text-white font-medium">Return for Amendments:</span> a reviewer sends it back for rework. Status resets to In Progress and a reason is required. See <Link href={pathFor("forms/return")} className="text-cyan-300 hover:underline">that walkthrough</Link>.</p>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-green-400 mt-0.5 shrink-0" />
            <p><span className="text-white font-medium">Close Task:</span> the item is done. A closing note is required and sign-off is checked.</p>
          </div>
          <div className="flex items-start gap-2">
            <Lock className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
            <p><span className="text-white font-medium">Cancel:</span> the item is no longer needed. This is final.</p>
          </div>
        </div>
        <Callout variant="info">
          The list of people in the Assigned To, Currently With, and Hand to dropdowns is managed under <span className="text-white">Admin → Team Members</span>.
        </Callout>
      </Section>

      <Section title="Some names are stages, not people">
        <p className="text-sm text-white/65 leading-relaxed">
          Six names — <span className="text-white">SDF MD</span>, <span className="text-white">SDF PMO</span>, <span className="text-white">SDF Board Secretary</span>, <span className="text-white">EDGE Chairman</span>, <span className="text-white">EDGE MD Office</span>, and <span className="text-white">Board / ExCom</span> — are workflow <em>stages</em>, not people you hand to with the inline dropdown. Picking them used to leave items stuck (no status change meant they never reached the MD Meetings queue), so they're now hidden from the inline Hand-to control on Updates and on the detail page, and from the sub-task assignee dropdown.
        </p>
        <p className="text-sm text-white/65 leading-relaxed">
          To send an item into one of these stages, use the green <Link href={pathFor("forms/send-for-review")} className="text-cyan-300 hover:underline">Send for Review</Link> button on Updates, or pick the proper destination status on the detail page. Both routes correctly pair the destination with the right receiving status in one combined action.
        </p>
      </Section>

      <Section title="Optional 'due back by' dates on handoffs">
        <p className="text-sm text-white/65 leading-relaxed">
          From v2.10 every handoff lets you optionally record a <span className="text-white">"Due back by"</span> date and a <span className="text-white">"Why this internal date?"</span> note. They appear on the history event, on the receiver's email, and on the Responsibility Timeline card — useful when the official due date doesn't match what you and the receiver actually expect. See <Link href={pathFor("handoff-deadlines")} className="text-cyan-300 hover:underline">Handoff Deadlines &amp; Fairness</Link>.
        </p>
      </Section>
    </div>
  );
}
